/**
 * The settings a household changes rather than a person (#1346).
 *
 * ⚠️ Shown to everybody, and only editable by somebody who may change them. A
 * member seeing a greyed control learns what the household has chosen; a member
 * seeing nothing learns that the app is inconsistent.
 */
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api } from "../lib/api";

interface Settings {
  household: {
    temperatureUnit: "C" | "F";
    learnFromRecipes: boolean;
  };
}

export function HouseholdSettings({ canEdit }: { canEdit: boolean }) {
  const qc = useQueryClient();
  const q = useQuery<Settings>({ queryKey: ["household"], queryFn: () => api("/household") });

  const save = useMutation({
    mutationFn: (body: Partial<Settings["household"]>) =>
      api("/household", { method: "PATCH", body: JSON.stringify(body) }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["household"] }),
  });

  const s = q.data?.household;
  if (!s) return null;

  return (
    <section className="border-line rounded-xl border p-5">
      <h2 className="font-semibold">Settings</h2>

      <div className="mt-4">
        <p className="text-sm font-medium">Oven temperatures</p>
        <p className="text-muted mt-0.5 text-sm">
          Recipes are written in Celsius. Pick whichever your oven is marked in.
        </p>
        <div className="mt-2 flex gap-1.5">
          {(["F", "C"] as const).map((u) => (
            <button
              key={u}
              onClick={() => canEdit && save.mutate({ temperatureUnit: u })}
              disabled={!canEdit || save.isPending}
              aria-pressed={s.temperatureUnit === u}
              className="border-line rounded-lg border px-3 py-1.5 text-sm font-medium disabled:opacity-50"
              style={
                s.temperatureUnit === u
                  ? { borderColor: "var(--color-plate-b)", color: "var(--color-plate-b)" }
                  : undefined
              }
            >
              {u === "F" ? "Fahrenheit (400°F)" : "Celsius (200°C)"}
            </button>
          ))}
        </div>
      </div>

      <div className="mt-5">
        <p className="text-sm font-medium">Recipes you add</p>
        {/* ⚠️ Says exactly what it does and what it never does. The Terms make
            this promise; this is where a household can hold us to it. */}
        <p className="text-muted mt-0.5 text-sm">
          We look at the shape of recipes you add — what goes in them and which
          parts can be served per plate — to decide what to add to our own
          library. <strong>We never publish your recipes, and we never show them
          to another household.</strong>
        </p>
        <label className="mt-2 flex items-start gap-2.5">
          <input
            type="checkbox"
            checked={s.learnFromRecipes}
            disabled={!canEdit || save.isPending}
            onChange={(e) => save.mutate({ learnFromRecipes: e.target.checked })}
            className="mt-1"
          />
          <span className="text-sm">
            Let Two Plates learn from the recipes we add
            <span className="text-muted block text-xs">
              Turning this off applies from now on. Nothing already learned
              identifies you — it is counted, not kept.
            </span>
          </span>
        </label>
      </div>

      {!canEdit && (
        <p className="text-muted mt-4 text-xs">
          Only the cook or the owner can change these.
        </p>
      )}
      {save.isError && (
        <p role="alert" className="text-warn mt-3 text-sm">That did not save. Try again.</p>
      )}
    </section>
  );
}
